<template>
  <div class="banner-content">
    <div class="toolbar">
      <el-input
        v-model="searchQuery"
        placeholder="搜索标题..."
        style="width: 300px"
        @input="filterData"
        clearable
      >
        <template #prefix>
          <el-icon><Search /></el-icon>
        </template>
      </el-input>
      <el-button type="primary" @click="showAddModal">新增轮播图</el-button>
    </div>

    <el-table :data="filteredList" style="width: 100%" border>
      <el-table-column prop="id" label="ID" width="80" />
      <el-table-column prop="title" label="标题/备注" min-width="120" show-overflow-tooltip />
      <el-table-column prop="image_path" label="图片" width="100">
        <template #default="scope">
          <el-image
            v-if="scope.row.image_path"
            :src="storageSrc(scope.row.image_path)"
            style="width: 72px; height: 40px"
            fit="cover"
          />
          <span v-else>—</span>
        </template>
      </el-table-column>
      <el-table-column prop="link_url" label="跳转链接" min-width="160" show-overflow-tooltip />
      <el-table-column prop="sort_order" label="排序" width="90" />
      <el-table-column prop="is_active" label="展示" width="100">
        <template #default="scope">
          <el-tag :type="Number(scope.row.is_active) === 1 ? 'success' : 'info'">
            {{ Number(scope.row.is_active) === 1 ? '是' : '否' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="180" fixed="right">
        <template #default="scope">
          <el-button type="primary" size="small" @click="editItem(scope.row)">编辑</el-button>
          <el-button type="danger" size="small" @click="deleteItem(scope.row.id)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog
      v-model="showModal"
      :title="isEditing ? '编辑轮播图' : '新增轮播图'"
      width="520px"
      destroy-on-close
    >
      <el-form ref="formRef" :model="formData" :rules="rules" label-width="100px">
        <el-form-item label="标题/备注" prop="title">
          <el-input v-model="formData.title" placeholder="可选" clearable />
        </el-form-item>
        <el-form-item label="轮播图片" prop="image_path">
          <el-upload
            class="upload-banner"
            action=""
            accept="image/*"
            :show-file-list="false"
            :http-request="handleImageHttpRequest"
            :before-upload="beforeImageUpload"
            :disabled="uploading"
          >
            <el-icon v-if="!formData.image_path && !uploading" class="upload-placeholder"><Plus /></el-icon>
            <el-icon v-if="uploading" class="upload-placeholder is-loading"><Loading /></el-icon>
            <el-image
              v-else-if="formData.image_path"
              :src="storageSrc(formData.image_path)"
              class="upload-preview"
              fit="cover"
            />
          </el-upload>
          <div class="upload-tip">建议上传横图；超过 2MB 将自动压缩后上传</div>
        </el-form-item>
        <el-form-item label="跳转链接" prop="link_url">
          <el-input v-model="formData.link_url" placeholder="选填，如 https://..." clearable />
        </el-form-item>
        <el-form-item label="排序" prop="sort_order">
          <el-input-number v-model="formData.sort_order" :min="0" :max="9999" controls-position="right" style="width: 100%" />
        </el-form-item>
        <el-form-item label="是否展示" prop="is_active">
          <el-switch v-model="formData.is_active" :active-value="1" :inactive-value="0" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="closeModal">取消</el-button>
        <el-button type="primary" :loading="submitting" @click="handleSubmit">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { Search, Plus, Loading } from '@element-plus/icons-vue'
import { getBannerList, addBanner, updateBanner, deleteBanner, uploadFile } from '@/api/index'
import { getUploadRelativePath, getStorageFileUrl, compressImageIfNeeded } from '@/utils/upload'

function emptyForm() {
  return {
    id: null,
    title: '',
    image_path: '',
    link_url: '',
    sort_order: 0,
    is_active: 1
  }
}

export default {
  name: 'BannerPage',
  components: { Search, Plus, Loading },
  data() {
    return {
      searchQuery: '',
      showModal: false,
      isEditing: false,
      list: [],
      filteredList: [],
      uploading: false,
      submitting: false,
      formData: emptyForm(),
      rules: {
        image_path: [{ required: true, message: '请上传轮播图片', trigger: 'change' }]
      }
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    storageSrc(path) {
      return getStorageFileUrl(path)
    },
    parseApiListPayload(res) {
      const d = res?.data
      if (!d) return []
      if (Array.isArray(d)) return d
      if (Array.isArray(d.list)) return d.list
      if (Array.isArray(d.data)) return d.data
      if (Array.isArray(d.items)) return d.items
      return []
    },
    normalizeRow(row) {
      const e = emptyForm()
      if (!row || typeof row !== 'object') return { ...e }
      const img = row.image_path ?? row.image ?? row.img ?? ''
      const active = row.is_active
      let ia = 1
      if (active === 0 || active === false || active === '0') ia = 0
      else if (active === 1 || active === true || active === '1') ia = 1
      else if (active != null) ia = Number(active) ? 1 : 0
      const sort = row.sort_order != null ? Number(row.sort_order) : 0
      return {
        id: row.id != null ? row.id : e.id,
        title: row.title != null ? String(row.title) : '',
        image_path: img ? String(img) : '',
        link_url: row.link_url != null ? String(row.link_url) : '',
        sort_order: Number.isFinite(sort) ? sort : 0,
        is_active: ia
      }
    },
    async loadData() {
      try {
        const res = await getBannerList()
        this.list = this.parseApiListPayload(res).map((r) => this.normalizeRow(r))
        this.filteredList = [...this.list]
      } catch (err) {
        console.error(err)
        this.$message.error('加载轮播图失败')
      }
    },
    filterData() {
      const q = this.searchQuery.trim().toLowerCase()
      if (!q) {
        this.filteredList = [...this.list]
        return
      }
      this.filteredList = this.list.filter((item) =>
        String(item.title || '')
          .toLowerCase()
          .includes(q)
      )
    },
    showAddModal() {
      this.isEditing = false
      this.formData = emptyForm()
      this.showModal = true
      this.$nextTick(() => this.$refs.formRef?.clearValidate())
    },
    editItem(row) {
      this.isEditing = true
      this.formData = this.normalizeRow(row)
      this.showModal = true
      this.$nextTick(() => this.$refs.formRef?.clearValidate())
    },
    deleteItem(id) {
      this.$confirm('确定删除该轮播图？', '提示', {
        type: 'warning',
        confirmButtonText: '确定',
        cancelButtonText: '取消'
      })
        .then(async () => {
          try {
            await deleteBanner(id)
            this.$message.success('删除成功')
            await this.loadData()
          } catch (e) {
            console.error(e)
            this.$message.error('删除失败')
          }
        })
        .catch(() => {})
    },
    beforeImageUpload(file) {
      const raw = file.raw != null ? file.raw : file
      if (!raw.type || !String(raw.type).includes('image')) {
        this.$message.error('只能上传图片')
        return false
      }
      return true
    },
    async handleImageHttpRequest(options) {
      const rawFile = options.file.raw != null ? options.file.raw : options.file
      this.uploading = true
      try {
        if (rawFile.size > 2 * 1024 * 1024) {
          this.$message.info('图片较大，正在压缩…')
        }
        const fileToUpload = await compressImageIfNeeded(rawFile)
        const fd = new FormData()
        fd.append('file', fileToUpload)
        const res = await uploadFile(fd)
        const rel = getUploadRelativePath(res)
        if (!rel) throw new Error('未返回图片路径')
        this.formData.image_path = rel
        if (typeof options.onSuccess === 'function') options.onSuccess(res)
        this.$message.success('上传成功')
        this.$refs.formRef?.validateField('image_path')
      } catch (e) {
        console.error(e)
        if (typeof options.onError === 'function') options.onError(e)
        this.$message.error('上传失败')
      } finally {
        this.uploading = false
      }
    },
    handleSubmit() {
      this.$refs.formRef.validate(async (valid) => {
        if (!valid) return
        this.submitting = true
        try {
          const payload = {
            title: this.formData.title || null,
            image_path: this.formData.image_path,
            link_url: this.formData.link_url || null,
            sort_order: this.formData.sort_order,
            is_active: this.formData.is_active
          }
          if (this.isEditing) {
            await updateBanner({ ...payload, id: this.formData.id })
            this.$message.success('保存成功')
          } else {
            await addBanner(payload)
            this.$message.success('新增成功')
          }
          await this.loadData()
          this.closeModal()
        } catch (e) {
          console.error(e)
          this.$message.error(this.isEditing ? '保存失败' : '新增失败')
        } finally {
          this.submitting = false
        }
      })
    },
    closeModal() {
      this.showModal = false
      this.formData = emptyForm()
    }
  }
}
</script>

<style scoped>
.banner-content {
  height: 100%;
}
.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  gap: 10px;
}
.upload-banner {
  width: 240px;
  height: 120px;
  border: 1px dashed #dcdfe6;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  overflow: hidden;
  transition: border-color 0.2s;
}
.upload-banner:hover {
  border-color: #409eff;
}
.upload-placeholder {
  font-size: 32px;
  color: #8c939d;
}
.upload-placeholder.is-loading {
  animation: spin 1s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
.upload-preview {
  width: 100%;
  height: 120px;
  display: block;
}
.upload-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 6px;
}
</style>
